/**
 * @author alteredq / http://alteredqualia.com/
 */
import * as THREE from "three";
import { checkWebGLContextLost } from "../../../higharc/checkWebGLContextLost";
import { EffectComposerPass } from "./EffectComposerPass";

export class RenderPass extends EffectComposerPass {
  clear = true;

  clearDepth = false;

  scissorRect: THREE.Box2 | undefined;
  scene: THREE.Scene | undefined;
  camera: THREE.Camera | undefined;
  overrideMaterial: THREE.Material | undefined;

  private clearColor: THREE.Color | undefined;
  clearAlpha: number;
  private clearStencil: number | undefined;

  private oldClearColor = new THREE.Color();

  constructor(
    scene: THREE.Scene | undefined,
    camera: THREE.Camera | undefined,
    overrideMaterial: THREE.Material | undefined,
    clearColor: THREE.Color | undefined,
    clearAlpha: number = 0,
    clearStencil: number | undefined = undefined,
  ) {
    super();

    this.scene = scene;
    this.camera = camera;

    this.overrideMaterial = overrideMaterial;

    this.clearColor = clearColor;
    this.clearAlpha = clearAlpha;
    this.clearStencil = clearStencil;
  }

  render(renderer: THREE.WebGLRenderer, writeBuffer: THREE.WebGLRenderTarget | null) {
    if (!this.scene || !this.camera) {
      return;
    }
    const oldAutoClear = renderer.autoClear;
    renderer.autoClear = false;

    this.scene.overrideMaterial = this.overrideMaterial !== undefined ? this.overrideMaterial : null;

    // A non-null scene.background makes three force a full clear of the bound target (and paint over it
    // for texture backgrounds), which ignores renderer.autoClear and would defeat `clear = false`.
    const oldBackground = this.scene.background;
    if (!this.clear) {
      this.scene.background = null;
    }

    let oldClearAlpha: number = 0;

    if (this.clearColor) {
      renderer.getClearColor(this.oldClearColor);
      oldClearAlpha = renderer.getClearAlpha();

      renderer.setClearColor(this.clearColor, this.clearAlpha);
    }

    try {
      renderer.setRenderTarget(writeBuffer);

      if (this.clearDepth) {
        renderer.clearDepth();
      }

      if (this.scissorRect) {
        renderer.setScissorTest(true);

        renderer.setScissor(
          this.scissorRect.min.x,
          this.scissorRect.min.y,
          this.scissorRect.max.x,
          this.scissorRect.max.y,
        );
      }

      if (this.clearStencil !== undefined) {
        renderer.getContext().clearStencil(this.clearStencil);
        renderer.clearStencil();
      }

      // TODO: Avoid using autoClear properties, see https://github.com/mrdoob/three.js/pull/15571#issuecomment-465669600
      if (this.clear) {
        renderer.clear(renderer.autoClearColor, renderer.autoClearDepth, renderer.autoClearStencil);
      }

      renderer.render(this.scene, this.camera);

      if (this.clearStencil !== undefined) {
        // assumes the stencil value is zero outside of renderpass
        renderer.getContext().clearStencil(0);
        renderer.clearStencil();
      }

      if (this.scissorRect) {
        renderer.setScissorTest(false);
      }
    } catch (e) {
      checkWebGLContextLost(renderer, e, "Rendering failed.");
    }

    if (this.clearColor) {
      renderer.setClearColor(this.oldClearColor, oldClearAlpha);
    }

    this.scene.overrideMaterial = null;
    this.scene.background = oldBackground;
    renderer.autoClear = oldAutoClear;
  }
}
